import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Avatar,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewerIcon,
  Person as MemberIcon,
  Shield as OwnerIcon,
  Business as CustomerIcon
} from '@mui/icons-material';
import { PageContainer, PageTitleContainer, Card } from '../components/common';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import projectService from '../services/projectService';
import userService from '../services/userService';
import roleService from '../services/roleService';
import PermissionGate from '../components/common/PermissionGate';
import useNotification from '../hooks/useNotification';
import useAuth from '../hooks/useAuth';

// Helper function to convert simple notation to notification object
const createNotification = (message, type = 'info') => ({
  message,
  type,
  duration: type === 'error' ? 6000 : 4000
});

const ProjectUsers = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user, hasPermission } = useAuth();

  // State management
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [project, setProject] = useState(null);

  // Form states
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [newMemberType, setNewMemberType] = useState('internal');
  const [newCanAssignValidators, setNewCanAssignValidators] = useState(false);
  const [editingRole, setEditingRole] = useState('');
  const [editingMemberType, setEditingMemberType] = useState('internal');
  const [editingCanAssign, setEditingCanAssign] = useState(false);

  // Project roles from API
  const [projectRoles, setProjectRoles] = useState([]);

  // User search states
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Fetch project details to determine user's role
  const fetchProject = async () => {
    try {
      const projectData = await projectService.getProjectById(projectId);
      setProject(projectData);
    } catch (err) {
      console.error('Error fetching project:', err);
    }
  };

  // Fetch project members
  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectService.getProjectMembers(projectId);
      setMembers(response.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.message || 'Failed to load project members');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users for the dropdown
  const fetchAllUsers = async () => {
    try {
      setUserSearchLoading(true);
      const response = await userService.getAllUsers();
      // Filter out users who are already members
      const existingMemberIds = members.map(m => m.user_id);
      const availableUsers = (response || []).filter(
        user => !existingMemberIds.includes(user.id || user._id)
      );
      setAllUsers(availableUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      // If the endpoint doesn't exist yet, we'll fall back to manual email entry
      setAllUsers([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchMembers();
    // Fetch project roles for the dropdown
    const fetchProjectRoles = async () => {
      try {
        const response = await roleService.getAllRoles('project');
        setProjectRoles(response?.roles || []);
      } catch (err) {
        console.error('Error fetching project roles:', err);
      }
    };
    fetchProjectRoles();
  }, [projectId]);

  // Check if current user can add members
  const canAddMembers = () => {
    if (!user || !project || members.length === 0) {
      return false;
    }

    const userId = user.id || user._id;
    const createdById = project.created_by?.id || project.created_by?._id || project.created_by;

    const isCreator = userId === createdById;

    const coOwners = project.co_owners || project.coOwners || [];
    const isCoOwner = coOwners.some(coOwnerId => {
      const coId = coOwnerId?.id || coOwnerId?._id || coOwnerId;
      return coId === userId;
    });

    const currentMember = members.find(m => {
      const memberId = m.user_id || m.userId;
      return memberId === userId;
    });

    const hasPermissionRole = currentMember && ['owner', 'admin'].includes(currentMember.role);
    const isSystemAdmin = user.role === 'admin';

    const hasManagePermission = hasPermission('project.members.manage');

    return (isCreator && (!currentMember || hasPermissionRole)) || hasPermissionRole || isSystemAdmin || isCoOwner || hasManagePermission;
  };

  // Filter visible members based on manage permission scope
  const visibleMembers = useMemo(() => {
    const canManageAll = hasPermission('project.members.manage', null, { exact: true });
    if (canManageAll) return members;

    const canManageInternal = hasPermission('project.members.manage.internal');
    const canManageExternal = hasPermission('project.members.manage.external');

    if (canManageInternal && canManageExternal) return members;

    return members.filter(member => {
      const type = member.member_type || 'internal';
      if (canManageInternal && type === 'internal') return true;
      if (canManageExternal && type === 'external') return true;
      // Always show the current user's own row
      const memberId = member.user_id || member.userId;
      const userId = user?.id || user?._id;
      if (memberId === userId) return true;
      return false;
    });
  }, [members, hasPermission, user]);

  // Fetch users when opening add member dialog
  useEffect(() => {
    if (addMemberDialogOpen) {
      fetchAllUsers();
    }
  }, [addMemberDialogOpen, members]);

  // Add member handler
  const handleAddMember = async () => {
    try {
      // Use selected user's email or the manually entered email
      const emailToAdd = selectedUser?.email || newMemberEmail;

      if (!emailToAdd) {
        showNotification(createNotification('Please select a user or enter an email address', 'error'));
        return;
      }

      const response = await projectService.addProjectMember(projectId, {
        email: emailToAdd,
        role: newMemberRole,
        member_type: newMemberType,
        can_assign_validators: newCanAssignValidators
      });

      // Check if this was an invitation
      const isInvitation = response?.invitation_created || response?.data?.invitation_created;
      let message;

      if (isInvitation) {
        message = `Invitation sent to ${emailToAdd}. They will receive an email to join the project.`;
      } else {
        message = response?.message || response?.data?.message || 'Member added successfully';
      }

      showNotification(createNotification(message, 'success'));
      setAddMemberDialogOpen(false);
      setNewMemberEmail('');
      setSelectedUser(null);
      setNewMemberRole('member');
      fetchMembers(); // Refresh the list
    } catch (err) {
      showNotification(createNotification(err.message || 'Failed to add member', 'error'));
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  // Remove member handler
  const handleRemoveMember = async () => {
    try {
      const userId = memberToDelete.user_id || memberToDelete.invitation_id;
      const response = await projectService.removeProjectMember(projectId, userId);

      // Handle both direct message and nested message structure
      const message = response?.message || response?.data?.message || 'Member removed successfully';
      showNotification(createNotification(message, 'success'));
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      fetchMembers(); // Refresh the list
    } catch (err) {
      showNotification(createNotification(err.message || 'Failed to remove member', 'error'));
    }
  };

  // Update role handler
  const handleUpdateRole = async () => {
    try {
      // Check if this is an invited member (no user_id or has invited status)
      if (!selectedMember.user_id || selectedMember.status === 'invited') {
        // For invited members, we need to update the invitation
        // For now, show a message that invitation roles can't be changed after sending
        showNotification(createNotification('Invitation role cannot be changed after sending. You can remove and re-invite with a different role.', 'info'));
        setEditMemberDialogOpen(false);
        setSelectedMember(null);
        setEditingRole('');
        return;
      }

      const response = await projectService.updateMemberRole(
        projectId,
        selectedMember.user_id,
        { role: editingRole, member_type: editingMemberType, can_assign_validators: editingCanAssign }
      );

      // Handle both direct message and nested message structure
      const message = response?.message || response?.data?.message || 'Member updated successfully';
      showNotification(createNotification(message, 'success'));
      setEditMemberDialogOpen(false);
      setSelectedMember(null);
      setEditingRole('');
      setEditingMemberType('internal');
      setEditingCanAssign(false);
      fetchMembers(); // Refresh the list
    } catch (err) {
      const errorMsg = err?.detail || err?.message || (typeof err === 'string' ? err : 'Failed to update role');
      showNotification(createNotification(errorMsg, 'error'));
    }
  };

  // Toggle member type handler
  const handleToggleMemberType = async (member) => {
    try {
      const newType = member.member_type === 'external' ? 'internal' : 'external';
      await projectService.updateMemberType(projectId, member.user_id, newType);
      showNotification(createNotification(`Member type updated to ${newType}`, 'success'));
      fetchMembers();
    } catch (err) {
      showNotification(createNotification(err.message || 'Failed to update member type', 'error'));
    }
  };

  // Toggle can_assign_validators handler
  const handleToggleCanAssign = async (member) => {
    try {
      const newValue = !member.can_assign_validators;
      await projectService.updateCanAssignValidators(projectId, member.user_id, newValue);
      showNotification(createNotification(`Assign validators permission ${newValue ? 'granted' : 'revoked'}`, 'success'));
      fetchMembers();
    } catch (err) {
      showNotification(createNotification(err.message || 'Failed to update permission', 'error'));
    }
  };

  // Role icon helper
  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <OwnerIcon sx={{ fontSize: 20 }} />;
      case 'admin':
        return <AdminIcon sx={{ fontSize: 20 }} />;
      case 'viewer':
        return <ViewerIcon sx={{ fontSize: 20 }} />;
      default:
        return <MemberIcon sx={{ fontSize: 20 }} />;
    }
  };

  // Role color helper
  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'viewer':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/projects/${projectId}`)}
        >
          Back to Project
        </Button>
      </PageContainer>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 4,
      overflowX: 'hidden'
    }}>
      <PageTitleContainer
        title="Project members"
        leftContent={
          <IconButton
            onClick={() => navigate(`/projects/${projectId}`)}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
        }
        rightContent={
          <PermissionGate permission="project.members.manage">
            {canAddMembers() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddMemberDialogOpen(true)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                padding: '10px 24px',
                backgroundColor: '#3182CE',
                color: 'white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: '#2C5282',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                },
                textTransform: 'none !important'
              }}
            >
              Add member
            </Button>
            )}
          </PermissionGate>
        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 0, width: '100%', }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '4px',
            p: { xs: 2, sm: 3, md: 4, lg: '20px' },
            width: '100%',
            bgcolor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            height: 'calc(100vh - 220px)'
          }}
        >
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '25%'
                  }}>User</TableCell>
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '30%'
                  }}>Email</TableCell>
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '15%'
                  }}>Role</TableCell>
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '12%'
                  }}>Type</TableCell>
                  {/* Can Assign column removed — now controlled via permission system
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%'
                  }}>Can Assign</TableCell>
                  */}
                  <TableCell sx={{
                    fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '12%'
                  }}>Added On</TableCell>
                  <PermissionGate permission="project.members.manage">
                    <TableCell sx={{
                      fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%'
                    }}>Actions</TableCell>
                  </PermissionGate>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleMembers.map((member) => (
                  <TableRow key={member.user_id || member.invitation_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: member.status === 'invited' ? 'grey.400' : undefined }}>
                          {member.name?.charAt(0).toUpperCase() || member.email.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant='body2'>
                          {member.name}
                          {member.is_creator && (
                            <Chip
                              label="Creator"
                              size="small"
                              color="primary"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                          {member.status === 'invited' && (
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell variant='body2'>{member.email}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(member.role)}
                        label={member.role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        size="small"
                        color={getRoleColor(member.role)}
                      />
                    </TableCell>
                    <TableCell variant='body2'>
                      {member.status !== 'invited' ? (
                        <Chip
                          label={member.member_type === 'external' ? 'External' : 'Internal'}
                          sx={{
                            cursor: canAddMembers() ? 'pointer' : 'default',
                            bgcolor: member.member_type === 'external' ? '#e8f5e9' : '#e3f2fd',
                            color: member.member_type === 'external' ? '#2e7d32' : '#1976d2',
                            border: `1px solid ${member.member_type === 'external' ? '#c8e6c9' : '#bbdefb'}`,
                            fontSize: '14px !important'
                          }}
                          onClick={() => {
                            if (canAddMembers() && !member.is_creator) {
                              handleToggleMemberType(member);
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    {/* Can Assign cell removed — now controlled via permission system */}
                    <TableCell variant='body2'>
                      {new Date(member.added_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <PermissionGate permission="project.members.manage">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={member.is_creator ? "Cannot edit creator role" : member.status === 'invited' ? "Cannot edit invited member role" : "Edit role"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setEditingRole(member.role);
                                  setEditingMemberType(member.member_type || 'internal');
                                  setEditingCanAssign(member.can_assign_validators || false);
                                  setEditMemberDialogOpen(true);
                                }}
                                disabled={member.is_creator || member.status === 'invited'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={member.is_creator ? "Cannot remove project creator" : "Remove Member"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(member)}
                                disabled={member.is_creator}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </PermissionGate>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add Member Dialog */}
        <Dialog
          open={addMemberDialogOpen}
          onClose={() => {
            setAddMemberDialogOpen(false);
            setSelectedUser(null);
            setNewMemberEmail('');
            setNewMemberRole('member');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            px: 4
          }}>Add project member</DialogTitle>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  Select user
                </Typography>
                <Autocomplete
                  options={allUsers}
                  getOptionLabel={(option) => `${option.name || ''} (${option.email})`}
                  value={selectedUser}
                  onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                    setNewMemberEmail(newValue?.email || '');
                  }}
                  loading={userSearchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type to search users..."
                      helperText="Select an existing user or enter any email address to send an invitation"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          height: '45px',
                          '& fieldset': {
                            borderColor: '#4759694d',
                            border: '1px solid #4759694d',
                            borderRadius: '4px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#475969',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          padding: '12px 14px',
                          color: 'var(--color-blue, #475969)',
                        },
                        '& .MuiFormHelperText-root': {
                          marginLeft: '4px',
                          fontSize: '12px'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {option.name?.charAt(0).toUpperCase() || option.email.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.email === value?.email}
                  noOptionsText="No users found"
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  Email address (will send invitation if user not found)
                </Typography>
                <TextField
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => {
                    setNewMemberEmail(e.target.value);
                    setSelectedUser(null); // Clear selected user when typing manually
                  }}
                  fullWidth
                  placeholder="Enter email address"
                  helperText="If the user is not in the list above, you can enter their email directly"
                  disabled={!!selectedUser}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      height: '45px',
                      '& fieldset': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  Role
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={newMemberRole}
                    onChange={(e) => setNewMemberRole(e.target.value)}
                    displayEmpty
                    MenuProps={{ PaperProps: { sx: { maxWidth: '100%', width: 'auto' } }, sx: { maxWidth: '100%' } }}
                    sx={{
                      borderRadius: '4px',
                      height: '45px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& .MuiSelect-select': {
                        fontSize: '14px',
                        color: 'var(--color-blue, #475969)',
                        padding: '12px 14px',
                      },
                    }}
                  >
                    {projectRoles.length > 0 ? projectRoles.map(r => (
                      <MenuItem key={r.role_id} value={r.role_id || r.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(r.role_id)}
                          {r.name}
                        </Box>
                      </MenuItem>
                    )) : (
                      <>
                        <MenuItem value="viewer">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ViewerIcon fontSize="small" />
                            Viewer - Can view project
                          </Box>
                        </MenuItem>
                        <MenuItem value="member">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MemberIcon fontSize="small" />
                            Member - Can view and edit
                          </Box>
                        </MenuItem>
                        <MenuItem value="admin">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AdminIcon fontSize="small" />
                            Admin - Can manage members
                          </Box>
                        </MenuItem>
                        <MenuItem value="owner">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OwnerIcon fontSize="small" />
                            Owner - Full control
                          </Box>
                        </MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#475969' }}>
                  Member Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={newMemberType}
                    onChange={(e) => setNewMemberType(e.target.value)}
                    sx={{
                      borderRadius: '4px',
                      height: '45px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& .MuiSelect-select': {
                        fontSize: '14px',
                        color: 'var(--color-blue, #475969)',
                        padding: '12px 14px',
                      },
                    }}
                  >
                    {(hasPermission('project.members.manage', null, { exact: true }) || hasPermission('project.members.manage.internal')) && (
                      <MenuItem value="internal">Internal</MenuItem>
                    )}
                    {(hasPermission('project.members.manage', null, { exact: true }) || hasPermission('project.members.manage.external')) && (
                      <MenuItem value="external">External</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* Can assign validators checkbox removed — now controlled via permission system */}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setAddMemberDialogOpen(false);
                setSelectedUser(null);
                setNewMemberEmail('');
                setNewMemberRole('member');
                setNewMemberType('internal');
                setNewCanAssignValidators(false);
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMember}
              variant="contained"
              disabled={!selectedUser && !newMemberEmail}
              sx={{
                textTransform: 'none',
                backgroundColor: '#3182CE',
                '&:hover': {
                  backgroundColor: '#2C5282'
                },
                textTransform: 'none !important'
              }}
            >
              Add member
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog
          open={editMemberDialogOpen}
          onClose={() => setEditMemberDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ px: 4, pt: 5 }}>Edit member role</DialogTitle>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: '#475969',
                  fontSize: '14px'
                }}
              >
                Updating role for: <strong>{selectedMember?.name || selectedMember?.email}</strong>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Role
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={editingRole}
                  onChange={(e) => setEditingRole(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: '4px',
                    height: '45px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      color: 'var(--color-blue, #475969)',
                      padding: '12px 14px',
                    },
                  }}
                >
                  {projectRoles.length > 0 ? projectRoles.map(r => (
                    <MenuItem key={r.role_id} value={r.role_id || r.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(r.role_id)}
                        {r.name}
                      </Box>
                    </MenuItem>
                  )) : (
                    <>
                      <MenuItem value="viewer">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ViewerIcon fontSize="small" />
                          Viewer - Can view project
                        </Box>
                      </MenuItem>
                      <MenuItem value="member">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MemberIcon fontSize="small" />
                          Member - Can view and edit
                        </Box>
                      </MenuItem>
                      <MenuItem value="admin">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AdminIcon fontSize="small" />
                          Admin - Can manage members
                        </Box>
                      </MenuItem>
                      <MenuItem value="owner">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <OwnerIcon fontSize="small" />
                          Owner - Full control
                        </Box>
                      </MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>

              <Typography
                variant="body1"
                sx={{
                  mt: 3, mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Member Type
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={editingMemberType}
                  onChange={(e) => setEditingMemberType(e.target.value)}
                  sx={{
                    borderRadius: '4px',
                    height: '45px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      color: 'var(--color-blue, #475969)',
                      padding: '12px 14px',
                    },
                  }}
                >
                  {(hasPermission('project.members.manage', null, { exact: true }) || hasPermission('project.members.manage.internal')) && (
                    <MenuItem value="internal">Internal</MenuItem>
                  )}
                  {(hasPermission('project.members.manage', null, { exact: true }) || hasPermission('project.members.manage.external')) && (
                    <MenuItem value="external">External</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Can assign validators checkbox removed — now controlled via permission system */}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setEditMemberDialogOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: '#3182CE',
                '&:hover': {
                  backgroundColor: '#2C5282'
                },
                textTransform: 'none !important'
              }}
            >
              Update role
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setMemberToDelete(null);
          }}
          title="Remove member from project"
          height="auto"
          content={
            <Box>
              <Typography variant="body1">
                Are you sure you want to remove {memberToDelete?.name || memberToDelete?.email} from this project?
              </Typography>
              {memberToDelete?.status === 'invited' && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  This will cancel the invitation sent to {memberToDelete?.email}.
                </Typography>
              )}
            </Box>
          }
          confirmText="Yes"
          cancelText="No"
          confirmColor="error"
          onConfirm={handleRemoveMember}
        />
      </Box>
    </Box>
  );
};

export default ProjectUsers;